import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { Collapse } from "antd";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { FaEdit } from "react-icons/fa";
import {
  doc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { COLLECTION_NAME } from "../../constants/endpoints";
import { firebaseTimeToTimestamp } from "../../utils";
import { toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

Modal.setAppElement("#root");

const UserDetailModal = ({ isOpen, onRequestClose, user }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isEditingTotalPaidOff, setIsEditingTotalPaidOff] = useState(false);
  const [newPaymentAmount, setNewPaymentAmount] = useState(0); // Amount to add/subtract
  const [paymentMode, setPaymentMode] = useState("add"); // "add" or "subtract"
  const [updateLoading, setUpdateLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [rideCounts, setRideCounts] = useState({
    currentMonth: 0,
    allTime: 0,
  });
  const [rideCountLoading, setRideCountLoading] = useState(false);
  const [calculatedEarnings, setCalculatedEarnings] = useState(0);
  const [totalTips, setTotalTips] = useState(0);
  const [earningsLoading, setEarningsLoading] = useState(false);

  const isDriver = ["Rider", "Driver"].includes(user?.role);

  const getEffectiveRideTimestamp = (rideData = {}) => {
    const isCompleted = rideData?.status === "completed";
    const isCancelled =
      "cancellationStatus" in rideData || !!rideData?.cancellationStatus;

    if (isCompleted) {
      const completedTimestamp = firebaseTimeToTimestamp(rideData?.updatedAt);
      if (Number.isFinite(completedTimestamp) && completedTimestamp > 0) {
        return completedTimestamp;
      }
    }

    if (isCancelled) {
      const cancelledTimestamp = firebaseTimeToTimestamp(rideData?.cancelledAt);
      if (Number.isFinite(cancelledTimestamp) && cancelledTimestamp > 0) {
        return cancelledTimestamp;
      }
    }

    const createdTimestamp = firebaseTimeToTimestamp(rideData?.createdAt);
    return Number.isFinite(createdTimestamp) ? createdTimestamp : 0;
  };

  const getCurrencyOrNA = (value) => {
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) return "N/A";
    return `$${parsedValue.toFixed(2)}`;
  };

  const getFormattedDateOrNA = (value) => {
    if (value == null) return "N/A";

    let timestamp = firebaseTimeToTimestamp(value);
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
      if (typeof value === "number" && Number.isFinite(value)) {
        timestamp = value > 1e12 ? value : value * 1000;
      } else if (typeof value === "string") {
        const parsed = Date.parse(value);
        timestamp = Number.isNaN(parsed) ? NaN : parsed;
      }
    }

    if (!Number.isFinite(timestamp) || timestamp <= 0) return "N/A";

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "N/A";

    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Fetch ride counts and calculate total earnings (driverPrice + tips) for the driver
  const fetchRideCounts = async () => {
    if (!isDriver || !user?.id) return;

    setRideCountLoading(true);
    setEarningsLoading(true);
    try {
      const ridesSnapshot = await getDocs(
        query(
          collection(db, COLLECTION_NAME.ridesHistory),
          where("riderId", "==", user.id)
        )
      );

      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();

      let currentMonthCount = 0;
      let allTimeCount = 0;
      let totalEarningsFromRides = 0;
      let totalTipsFromRides = 0;

      ridesSnapshot.docs.forEach((doc) => {
        const rideData = doc.data();
        const isCompleted = rideData?.status === "completed";
        const isCancelled =
          "cancellationStatus" in rideData || !!rideData?.cancellationStatus;

        // Keep earnings/counts aligned with ride history:
        // include only completed or cancelled rides.
        if (!isCompleted && !isCancelled) {
          return;
        }

        allTimeCount++;

        // Sum up driverPrice and tips for total earnings calculation
        const driverPrice = parseFloat(rideData.driverPrice) || 0;
        const tip = parseFloat(rideData.tip) || 0;
        totalEarningsFromRides += driverPrice;
        totalTipsFromRides += tip;

        // Use completed/cancelled timestamp for monthly bucketing.
        const effectiveRideTimestamp = getEffectiveRideTimestamp(rideData);
        if (Number.isFinite(effectiveRideTimestamp) && effectiveRideTimestamp > 0) {
          const rideDate = new Date(effectiveRideTimestamp);
          if (
            rideDate.getMonth() === currentMonth &&
            rideDate.getFullYear() === currentYear
          ) {
            currentMonthCount++;
          }
        }
      });

      setRideCounts({
        currentMonth: currentMonthCount,
        allTime: allTimeCount,
      });

      setCalculatedEarnings(totalEarningsFromRides);
      setTotalTips(totalTipsFromRides);
    } catch (error) {
      console.error("Error fetching ride counts:", error);
      toast.error("Failed to load ride counts", {
        position: "top-right",
        autoClose: 3000,
        transition: Bounce,
      });
    } finally {
      setRideCountLoading(false);
      setEarningsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && isDriver && user?.id) {
      fetchRideCounts();
    }
  }, [isOpen, isDriver, user?.id]);

  if (!user) return null;

  const handleUpdateTotalPaidOff = async () => {
    const totalEarning = (calculatedEarnings || 0) + (totalTips || 0);
    const currentTotalPaidOff = user.totalPaidOff || 0;
    const paymentAmount = parseFloat(newPaymentAmount);

    // Calculate new total based on mode
    const newTotalPaidOff =
      paymentMode === "add"
        ? currentTotalPaidOff + paymentAmount
        : currentTotalPaidOff - paymentAmount;

    // Clear previous validation errors
    setValidationError("");

    // Validation: Check for empty or invalid input
    if (
      isNaN(paymentAmount) ||
      newPaymentAmount === "" ||
      newPaymentAmount === "0"
    ) {
      setValidationError(`Please enter a valid amount to ${paymentMode}`);
      toast.error(`Please enter a valid amount to ${paymentMode}`, {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
      return;
    }

    // Validation: Check for negative values in input
    if (paymentAmount < 0) {
      setValidationError("Amount cannot be negative");
      toast.error("Amount cannot be negative", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
      return;
    }

    // Validation: Check if subtracting would result in negative total
    if (paymentMode === "subtract" && newTotalPaidOff < 0) {
      setValidationError(
        `Cannot subtract more than current total ($${currentTotalPaidOff.toFixed(
          2
        )})`
      );
      toast.error(
        `Cannot subtract $${paymentAmount.toFixed(
          2
        )} - would result in negative balance`,
        {
          position: "top-right",
          autoClose: 6000,
          transition: Bounce,
        }
      );
      return;
    }

    // Validation: Check if adding would exceed total earning
    if (paymentMode === "add" && newTotalPaidOff > totalEarning) {
      const maxAllowed = totalEarning - currentTotalPaidOff;
      const errorMsg = `Cannot exceed remaining earning ($${maxAllowed.toFixed(
        2
      )})`;
      setValidationError(errorMsg);
      toast.error(
        `Cannot add $${paymentAmount.toFixed(2)} - ${errorMsg.toLowerCase()}`,
        {
          position: "top-right",
          autoClose: 6000,
          transition: Bounce,
        }
      );
      return;
    }

    setUpdateLoading(true);
    try {
      const userRef = doc(db, COLLECTION_NAME.users, user.id);
      await updateDoc(userRef, { totalPaidOff: newTotalPaidOff });

      const actionText = paymentMode === "add" ? "Added" : "Subtracted";
      const operationText = paymentMode === "add" ? "to" : "from";

      toast.success(
        `${actionText} $${paymentAmount.toFixed(
          2
        )} ${operationText} Total Paid Off! New total: $${newTotalPaidOff.toFixed(
          2
        )}`,
        {
          position: "top-right",
          autoClose: 6000,
          transition: Bounce,
        }
      );

      setIsEditingTotalPaidOff(false);
      setValidationError("");
      setNewPaymentAmount(0);
      setPaymentMode("add"); // Reset to add mode
      // Update the user object to reflect the change
      user.totalPaidOff = newTotalPaidOff;
    } catch (error) {
      console.error("Error updating totalPaidOff: ", error);
      toast.error("Failed to update Total Paid Off. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const UserDetailItem = ({ label, value, type }) => (
    <div className="flex items-center justify-between py-2 px-4 border-b border-gray-200">
      <strong className="text-gray-700 font-semibold">{label}:</strong>
      {type === "text" ? (
        <span className="text-gray-500">{value}</span>
      ) : (
        <img
          src={value}
          alt={label}
          className="h-10 w-10 rounded-full object-cover cursor-pointer"
          onClick={() => setSelectedImage(value)}
        />
      )}
    </div>
  );

  const EarningsItem = ({
    label,
    value,
    isEditable = false,
    onEdit,
    isEditing,
    newValue,
    onValueChange,
    onSave,
    onCancel,
    loading,
    validationError = "",
    isCurrency = true, // New prop to distinguish between currency and number values
  }) => {
    const formatValue = (val) => {
      if (isCurrency) {
        // For currency values
        return typeof val === "number" ? `$${val.toFixed(2)}` : "$0.00";
      } else {
        // For number values (like ride counts)
        return typeof val === "number" ? val.toString() : "0";
      }
    };

    return (
      <div className="py-2 px-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <strong className="text-gray-700 font-semibold">{label}:</strong>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only numbers and one decimal point
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        onValueChange(value);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Allow Enter key to save
                      if (e.key === "Enter") {
                        onSave();
                      }
                      // Allow Escape key to cancel
                      if (e.key === "Escape") {
                        onCancel();
                      }
                    }}
                    className={`w-28 px-3 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-dazzledBlue focus:border-transparent ${
                      validationError ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="0.00"
                    autoFocus
                  />
                  <button
                    onClick={onSave}
                    disabled={loading}
                    className="px-3 py-1 bg-celadon text-primary rounded-md text-xs font-medium hover:bg-celadon/90 disabled:opacity-50 transition-colors duration-200"
                  >
                    {loading ? "..." : "Save"}
                  </button>
                  <button
                    onClick={onCancel}
                    disabled={loading}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-300 disabled:opacity-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
                {validationError && (
                  <span className="text-red-500 text-xs">
                    {validationError}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-gray-500 font-medium">
                  {formatValue(value)}
                </span>
                {isEditable && (
                  <button
                    onClick={onEdit}
                    className="text-primary hover:text-dazzledBlue p-1 transition-colors duration-200"
                    title="Add Payment"
                  >
                    <FaEdit size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const CumulativePaymentItem = ({
    label,
    value,
    onEdit,
    isEditing,
    newPaymentAmount,
    onValueChange,
    onSave,
    onCancel,
    loading,
    validationError = "",
    paymentMode,
    onModeChange,
  }) => (
    <div className="py-2 px-4 border-b border-gray-200">
      <div className="flex items-center justify-between">
        <strong className="text-gray-700 font-semibold">{label}:</strong>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <div className="flex flex-col items-end gap-1">
                  <span className="text-xs text-gray-400">
                    Current:{" "}
                    {typeof value === "number"
                      ? `$${value.toFixed(2)}`
                      : "$0.00"}
                  </span>
                  <div className="flex items-center gap-2">
                    {/* Mode Toggle */}
                    <div className="flex bg-gray-100 rounded-md p-1">
                      <button
                        type="button"
                        onClick={() => onModeChange("add")}
                        className={`px-2 py-1 text-xs rounded transition-colors duration-200 ${
                          paymentMode === "add"
                            ? "bg-celadon text-primary font-medium"
                            : "text-gray-600 hover:text-primary"
                        }`}
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        onClick={() => onModeChange("subtract")}
                        className={`px-2 py-1 text-xs rounded transition-colors duration-200 ${
                          paymentMode === "subtract"
                            ? "bg-red-100 text-red-600 font-medium"
                            : "text-gray-600 hover:text-red-600"
                        }`}
                      >
                        Subtract
                      </button>
                    </div>
                    <input
                      type="text"
                      value={newPaymentAmount}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        // Allow only numbers and one decimal point
                        if (
                          inputValue === "" ||
                          /^\d*\.?\d*$/.test(inputValue)
                        ) {
                          onValueChange(inputValue);
                        }
                      }}
                      onKeyDown={(e) => {
                        // Allow Enter key to save
                        if (e.key === "Enter") {
                          onSave();
                        }
                        // Allow Escape key to cancel
                        if (e.key === "Escape") {
                          onCancel();
                        }
                      }}
                      className={`w-28 px-3 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-dazzledBlue focus:border-transparent ${
                        validationError ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="0.00"
                      autoFocus
                    />
                  </div>
                </div>
                <button
                  onClick={onSave}
                  disabled={loading}
                  className={`px-3 py-1 rounded-md text-xs font-medium disabled:opacity-50 transition-colors duration-200 ${
                    paymentMode === "add"
                      ? "bg-celadon text-primary hover:bg-celadon/90"
                      : "bg-red-500 text-white hover:bg-red-600"
                  }`}
                >
                  {loading ? "..." : paymentMode === "add" ? "Add" : "Subtract"}
                </button>
                <button
                  onClick={onCancel}
                  disabled={loading}
                  className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-300 disabled:opacity-50 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
              {validationError && (
                <span className="text-red-500 text-xs">{validationError}</span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-gray-500 font-medium">
                {typeof value === "number" ? `$${value.toFixed(2)}` : "$0.00"}
              </span>
              <button
                onClick={onEdit}
                className="text-primary hover:text-dazzledBlue p-1 transition-colors duration-200"
                title="Adjust Payment"
              >
                <FaEdit size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const userDetails = [
    { label: "Name", value: user.fullName, type: "text" },
    { label: "Email", value: user.email, type: "text" },
    { label: "Phone", value: user.phoneNumber, type: "text" },
    {
      label: "Role",
      value: ["Rider", "Driver"].includes(user.role) ? "Driver" : user.role,
      type: "text",
    },
    { label: "Rating", value: user.rating, type: "text" },
  ];

  // Add driver license for drivers
  if (!isDriver) {
    userDetails.push({
      label: "Driver License / Passport",
      value: user?.idCardPicture,
      type: "image",
    });
  }

  const getCarDetails = (vehicle) => [
    { label: "Car Make", value: vehicle?.carMake, type: "text" },
    { label: "Car Model", value: vehicle?.carModal, type: "text" },
    { label: "Car Color", value: vehicle?.carColor, type: "text" },
    { label: "Car Plate", value: vehicle?.carPlate, type: "text" },
    { label: "Vehicle Type", value: vehicle?.vehicleType, type: "text" },
    {
      label: "Car Insurance Info",
      value: vehicle?.carInsuranceInfo,
      type: "image",
    },
    { label: "Taxi License", value: vehicle?.taxiLicense, type: "image" },
    {
      label: "Driver License",
      value: vehicle?.driverLicense,
      type: "image",
    },
  ];

  let carDetailsArray = [];

  if (isDriver && user.vehicles && user.vehicles.length > 0) {
    carDetailsArray = user.vehicles.map(getCarDetails);
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="User Details"
      className="relative bg-white rounded-xl shadow-xl p-6 max-w-3xl mt-16 mx-auto max-h-[80%] overflow-y-auto custom-scrollbar"
      overlayClassName="fixed inset-0 bg-black bg-opacity-70"
    >
      <button
        onClick={onRequestClose}
        className="absolute top-4 right-4 text-red-500 hover:text-red-600"
      >
        <IoMdCloseCircleOutline size={26} />
      </button>

      <div className="flex flex-col items-center mb-6">
        <img
          src={user.profilePicture}
          alt="Profile"
          className="w-32 h-32 rounded-full shadow-lg mb-4 cursor-pointer"
          onClick={() => setSelectedImage(user.profilePicture)}
        />
        <h2 className="text-dazzledBlue text-2xl font-bold">
          {isDriver ? "Driver" : "Passenger"} Details
        </h2>
      </div>

      <div className="bg-gray-100 rounded-lg shadow-lg p-4 mb-6">
        <h3 className="text-xl font-semibold text-dazzledBlue mb-3 pb-2">
          Personal Details
        </h3>
        {userDetails.map(({ label, value, type }, index) => (
          <UserDetailItem key={index} label={label} value={value} type={type} />
        ))}

        {/* Earnings Section - Only for drivers */}
        {isDriver && (
          <>
            <EarningsItem
              label="Driver Price (from Rides)"
              value={earningsLoading ? 0 : calculatedEarnings}
              isEditable={false}
            />
            <EarningsItem
              label="Total Tips"
              value={earningsLoading ? 0 : totalTips}
              isEditable={false}
            />
            <EarningsItem
              label="Total Earning (Driver Price + Tips)"
              value={earningsLoading ? 0 : calculatedEarnings + totalTips}
              isEditable={false}
            />
            <CumulativePaymentItem
              label="Total Paid Off"
              value={user.totalPaidOff || 0}
              onEdit={() => {
                setIsEditingTotalPaidOff(true);
                setValidationError("");
                setNewPaymentAmount(0);
                setPaymentMode("add"); // Reset to add mode when editing
              }}
              isEditing={isEditingTotalPaidOff}
              newPaymentAmount={newPaymentAmount}
              onValueChange={setNewPaymentAmount}
              onSave={handleUpdateTotalPaidOff}
              onCancel={() => {
                setIsEditingTotalPaidOff(false);
                setNewPaymentAmount(0);
                setValidationError("");
                setPaymentMode("add"); // Reset to add mode on cancel
              }}
              loading={updateLoading}
              validationError={validationError}
              paymentMode={paymentMode}
              onModeChange={setPaymentMode}
            />
            <UserDetailItem
              label="Previous Week Income"
              value={getCurrencyOrNA(user?.previousWeekIncome)}
              type="text"
            />
            <UserDetailItem
              label="Wallet Reset At"
              value={getFormattedDateOrNA(user?.walletResetAt)}
              type="text"
            />
            <EarningsItem
              label="Remaining Balance"
              value={
                (earningsLoading ? 0 : calculatedEarnings + totalTips) -
                (user.totalPaidOff || 0)
              }
              isEditable={false}
            />
            <EarningsItem
              label="Total Rides This Month"
              value={rideCountLoading ? 0 : rideCounts.currentMonth}
              isEditable={false}
              isCurrency={false}
            />
            <EarningsItem
              label="All Time Rides"
              value={rideCountLoading ? 0 : rideCounts.allTime}
              isEditable={false}
              isCurrency={false}
            />
          </>
        )}
      </div>

      {isDriver && user.bankDetails && (
        <div className="bg-gray-100 rounded-lg shadow-lg p-4 mb-6">
          <h3 className="text-xl font-semibold text-dazzledBlue mb-3 pb-2">
            Bank Details
          </h3>
          <div className="flex items-center justify-between py-2 px-4 border-b border-gray-200">
            <strong className="text-gray-700 font-semibold">Full Name:</strong>
            <span className="text-gray-500">{user.bankDetails?.fullname}</span>
          </div>
          <div className="flex items-center justify-between py-2 px-4 border-b border-gray-200">
            <strong className="text-gray-700 font-semibold">Bank Name:</strong>
            <span className="text-gray-500">{user.bankDetails?.bankname}</span>
          </div>
          <div className="flex items-center justify-between py-2 px-4 border-b border-gray-200">
            <strong className="text-gray-700 font-semibold">
              Account Number:
            </strong>
            <span className="text-gray-500">
              {user.bankDetails?.accountnumber}
            </span>
          </div>
          <div className="flex items-center justify-between py-2 px-4 border-b border-gray-200">
            <strong className="text-gray-700 font-semibold">
              Routing Number:
            </strong>
            <span className="text-gray-500">{user.bankDetails?.swiftcode}</span>
          </div>

          <div className="flex items-center justify-between py-2 px-4 border-b border-gray-200">
            <strong className="text-gray-700 font-semibold">
              Peer Mobile Id:
            </strong>
            <span className="text-gray-500">
              {user?.bankDetails?.peerMobileId
                ? user?.bankDetails?.peerMobileId
                : "N/A"}
            </span>
          </div>

          <div className="flex items-center justify-between py-2 px-4 border-b border-gray-200">
            <strong className="text-gray-700 font-semibold">
              Account Type:
            </strong>
            <span className="text-gray-500">
              {user?.bankDetails?.accountType
                ? user?.bankDetails?.accountType
                : "N/A"}
            </span>
          </div>
        </div>
      )}

      {isDriver && carDetailsArray.length > 0 && (
        <div className="bg-gray-100 rounded-lg shadow-lg p-4">
          <h3 className="text-xl font-semibold text-dazzledBlue mb-3 pb-2">
            Car Details
          </h3>

          <Collapse accordion defaultActiveKey={["0"]}>
            {carDetailsArray.map((carDetails, idx) => (
              <Collapse.Panel
                header={
                  <span className="font-bold">{`Vehicle #${idx + 1}`}</span>
                }
                key={idx.toString()}
              >
                {carDetails.map(({ label, value, type }, index) => (
                  <UserDetailItem
                    key={index}
                    label={label}
                    value={value}
                    type={type}
                  />
                ))}
              </Collapse.Panel>
            ))}
          </Collapse>
        </div>
      )}

      {selectedImage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50">
          <div className="relative">
            <img
              src={selectedImage}
              alt="Full View"
              className="max-h-[70vh] rounded-lg shadow-lg"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-1 right-3 text-3xl text-red-700"
            >
              &times;
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UserDetailModal;

